import {
  Connection,
  CreateConnectionDto,
  UpdateConnectionDto,
  TestConnectionDto,
  TestConnectionResponse,
  ConnectionAccessResponse,
  ServiceAccountUploadDto,
  ConnectionUsageInstructionsResponse,
} from '@optidev/shared-types';

import { apiClient, createAuthHeaders } from '../api-client';

export interface ConnectionFilters {
  type?: string;                    // Connector ID (e.g., 'postgresql', 'mongodb')
  status?: string;
  enabled?: boolean;
}

export const connectionsApi = {
  /**
   * Get all connections for the current workspace
   */
  async getAll(
    token: string,
    filters?: ConnectionFilters,
  ): Promise<Connection[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.enabled !== undefined) params.append('enabled', String(filters.enabled));

    const queryString = params.toString();
    const url = queryString ? `/api/connections?${queryString}` : '/api/connections';

    const response = await apiClient.get(url, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single connection by ID
   */
  async getOne(token: string, id: string): Promise<Connection> {
    const response = await apiClient.get(`/api/connections/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Create a new connection
   */
  async create(token: string, dto: CreateConnectionDto): Promise<Connection> {
    const response = await apiClient.post('/api/connections', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Update an existing connection
   */
  async update(
    token: string,
    id: string,
    dto: UpdateConnectionDto,
  ): Promise<Connection> {
    const response = await apiClient.patch(`/api/connections/${id}`, dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Delete a connection
   */
  async delete(token: string, id: string): Promise<void> {
    await apiClient.delete(`/api/connections/${id}`, {
      headers: createAuthHeaders(token),
    });
  },

  /**
   * Test a connection before creating/updating
   */
  async test(
    token: string,
    dto: TestConnectionDto,
  ): Promise<TestConnectionResponse> {
    const response = await apiClient.post('/api/connections/test', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Test an existing connection by ID
   */
  async testExisting(
    token: string,
    id: string,
  ): Promise<TestConnectionResponse> {
    const response = await apiClient.post(`/api/connections/${id}/test`, {}, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get access credentials for a connection (for runtime use)
   */
  async getAccess(
    token: string,
    id: string,
  ): Promise<ConnectionAccessResponse> {
    const response = await apiClient.post(`/api/connections/${id}/access`, {}, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get usage instructions for a connection (markdown with real values)
   */
  async getUsageInstructions(
    token: string,
    id: string,
  ): Promise<ConnectionUsageInstructionsResponse> {
    const response = await apiClient.get(`/api/connections/${id}/usage-instructions`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Refresh OAuth tokens for a connection
   */
  async refreshTokens(
    token: string,
    id: string,
  ): Promise<Connection> {
    const response = await apiClient.post(`/api/connections/${id}/refresh`, {}, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Upload service account credentials (e.g., Google service account JSON)
   */
  async uploadServiceAccount(
    token: string,
    dto: ServiceAccountUploadDto,
  ): Promise<Connection> {
    const response = await apiClient.post('/api/connections/service-account', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Toggle connection enabled status
   */
  async toggleEnabled(
    token: string,
    id: string,
    enabled: boolean,
  ): Promise<Connection> {
    const response = await apiClient.patch(`/api/connections/${id}`, { enabled }, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Initiate OAuth flow for a connector
   */
  async initiateOAuth(
    token: string,
    connectorId: string,
    connectionName?: string,
    scopes?: string[],
    authMethod?: string,
    additionalParams?: Record<string, string>,
  ): Promise<{ authUrl: string; state: string }> {
    const response = await apiClient.post('/api/connections/oauth/init', {
      connectorId,
      connectionName,
      scopes,
      authMethod,
      additionalParams,
    }, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Initiate OAuth reauthorization for an existing connection
   * Used to switch accounts or update scopes
   */
  async initiateReauthorization(
    token: string,
    connectionId: string,
    scopes?: string[],
  ): Promise<{ authUrl: string; state: string }> {
    const response = await apiClient.post(
      `/api/connections/${connectionId}/reauthorize`,
      { scopes },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Exchange OAuth code for connection
   * Called by frontend after receiving OAuth callback from provider
   */
  async exchangeOAuthCode(
    code: string,
    state: string,
  ): Promise<{ connectionId: string; connectorId: string; connectorName: string; reauthorized?: boolean }> {
    const response = await apiClient.post('/api/connections/oauth/exchange', {
      code,
      state,
    });
    return response.data;
  },
};
